import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { postsApi, approvalsApi, socialAccountsApi, type Post, type SocialAccount } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { formatRelativeTime, parseDbDate } from '@/lib/utils';
import { Trash2, Copy, Search, CheckCircle2, XCircle, Clock, Send, Pencil, RotateCw, Recycle, AlertCircle, RefreshCw } from 'lucide-react';
import { PLATFORMS, type Platform } from '@/lib/platforms';
import { Button } from '@/components/ui/Button';
import { PlatformIcon } from '@/components/ui/PlatformIcon';

const PAGE_SIZE = 50;

type TargetState = 'success' | 'failed' | 'pending';

interface PostTarget {
  id: string;
  /** Platform key for the logo; null when the account is gone and no result recorded it. */
  platform: string | null;
  label: string;
  state: TargetState;
  error: string | null;
}

// One entry per target account: a readable name (never the raw UUID) plus the
// latest publish outcome from post.results.
function postTargets(post: Post, accounts: Map<string, SocialAccount>): PostTarget[] {
  let ids: string[] = [];
  try {
    ids = JSON.parse(post.platforms || '[]') as string[];
  } catch {
    /* malformed platforms */
  }
  const results = new Map((post.results ?? []).map((r) => [r.social_account_id, r]));
  return ids.map((id) => {
    const acc = accounts.get(id);
    const result = results.get(id);
    const platform = acc?.platform ?? result?.platform ?? null;
    const platformLabel = platform ? PLATFORMS[platform as Platform]?.label ?? platform : null;
    const name = acc?.account_name ?? result?.account_name ?? null;
    const label = acc
      ? `${platformLabel} · ${name}`
      : name
        ? `${platformLabel ? `${platformLabel} · ` : ''}${name} (disconnected)`
        : 'Disconnected account';
    return {
      id,
      platform,
      label,
      state: result?.status ?? 'pending',
      error: result?.status === 'failed' ? result.error_message : null,
    };
  });
}

const TARGET_COLORS: Record<TargetState, string> = {
  success: 'bg-success/10 text-success',
  failed: 'bg-destructive/10 text-destructive',
  pending: 'bg-muted text-muted-foreground',
};

const TABS = [
  { label: 'All', value: 'all' },
  { label: 'Drafts', value: 'draft' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Published', value: 'published' },
  { label: 'Pending Approval', value: 'pending_approval' },
  { label: 'Failed', value: 'failed' },
];

function tabFromParam(param: string | null): string | null {
  const value = param === 'pending' ? 'pending_approval' : param;
  return TABS.some((t) => t.value === value) ? value : null;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  queued: 'bg-info/10 text-info',
  scheduled: 'bg-accent/10 text-accent',
  publishing: 'bg-warning/10 text-warning',
  published: 'bg-success/10 text-success',
  failed: 'bg-destructive/10 text-destructive',
  recurring: 'bg-info/10 text-info',
};

interface PostManagerProps {
  /** Open a post in the composer for editing (also used by "Post again"). */
  onEdit?: (postId: string) => void;
}

export function PostManager({ onEdit }: PostManagerProps) {
  const { getToken } = useAuth();
  const { currentWorkspace } = useAppStore();
  const qc = useQueryClient();

  // ?tab= deep-links (approval notifications use ?tab=pending) pick the tab,
  // then the param is dropped so following the same link again re-applies it.
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => tabFromParam(searchParams.get('tab')) ?? 'all');
  useEffect(() => {
    const param = searchParams.get('tab');
    if (!param) return;
    const tab = tabFromParam(param);
    if (tab) setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rejectNote, setRejectNote] = useState('');
  const [rejectingId, setRejectingId] = useState<string | null>(null);

  const [actionError, setActionError] = useState<string | null>(null);

  const isPendingApprovalTab = activeTab === 'pending_approval';

  // Search runs on the server (across every post, not just the loaded page);
  // debounce so typing doesn't fire a request per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const listQuery = useInfiniteQuery({
    queryKey: ['posts', currentWorkspace?.id, 'manager', activeTab, debouncedSearch],
    queryFn: async ({ pageParam }) => {
      const token = await getToken();
      return postsApi.list(token!, {
        workspaceId: currentWorkspace!.id,
        status: activeTab === 'all' ? undefined : activeTab,
        q: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset: pageParam,
      });
    },
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.posts.length, 0);
      return loaded < last.total && last.posts.length > 0 ? loaded : undefined;
    },
    enabled: !!currentWorkspace && !isPendingApprovalTab,
  });

  const pendingQuery = useQuery({
    queryKey: ['posts', currentWorkspace?.id, 'pending_approval'],
    queryFn: async () => {
      const token = await getToken();
      return approvalsApi.listPending(token!, currentWorkspace!.id);
    },
    enabled: !!currentWorkspace && isPendingApprovalTab,
  });

  const isLoading = isPendingApprovalTab ? pendingQuery.isLoading : listQuery.isLoading;
  const total = listQuery.data?.pages[listQuery.data.pages.length - 1]?.total ?? 0;

  // Target accounts → readable names for the per-account chips.
  const { data: accountsData } = useQuery({
    queryKey: ['social-accounts', currentWorkspace?.id],
    queryFn: async () => {
      const token = await getToken();
      return socialAccountsApi.list(token!, currentWorkspace!.id);
    },
    enabled: !!currentWorkspace,
  });
  const accountsById = useMemo(
    () => new Map((accountsData?.accounts ?? []).map((a) => [a.id, a])),
    [accountsData]
  );

  // Re-publish only to the accounts that failed; succeeded ones are skipped.
  const retryFailed = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return postsApi.retry(token!, id);
    },
    onMutate: () => setActionError(null),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['posts', currentWorkspace?.id] }),
    onError: (err) => setActionError(err instanceof Error ? err.message : 'Retry failed'),
  });

  const deletePost = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return postsApi.delete(token!, id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['posts', currentWorkspace?.id] }),
  });

  const duplicatePost = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return postsApi.duplicate(token!, id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['posts', currentWorkspace?.id] }),
  });

  // Evergreen toggle: recycled automatically by the daily cron every
  // recycle_interval_days, up to max_recycles times.
  const toggleEvergreen = useMutation({
    mutationFn: async ({ id, on }: { id: string; on: boolean }) => {
      const token = await getToken();
      return postsApi.update(token!, id, { is_evergreen: on } as never);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['posts', currentWorkspace?.id] }),
  });

  // "Post again": duplicate a published post and open the fresh copy in the
  // composer to tweak and re-schedule.
  const postAgain = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return postsApi.duplicate(token!, id);
    },
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ['posts', currentWorkspace?.id] });
      if (resp.post?.id) onEdit?.(resp.post.id);
    },
  });

  const bulkDelete = useMutation({
    mutationFn: async (ids: string[]) => {
      const token = await getToken();
      return postsApi.bulk(token!, { postIds: ids, action: 'delete' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posts', currentWorkspace?.id] });
      setSelected(new Set());
    },
  });

  const submitForApproval = useMutation({
    mutationFn: async (postId: string) => {
      const token = await getToken();
      return approvalsApi.submit(token!, postId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['posts', currentWorkspace?.id] }),
  });

  const approvePost = useMutation({
    mutationFn: async (postId: string) => {
      const token = await getToken();
      return approvalsApi.approve(token!, postId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['posts', currentWorkspace?.id] }),
  });

  const rejectPost = useMutation({
    mutationFn: async ({ postId, note }: { postId: string; note: string }) => {
      const token = await getToken();
      return approvalsApi.reject(token!, postId, note || undefined);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posts', currentWorkspace?.id] });
      setRejectingId(null);
      setRejectNote('');
    },
  });

  const posts: Post[] = isPendingApprovalTab
    ? (pendingQuery.data?.posts ?? []).filter(
        (p) => !search || p.content.toLowerCase().includes(search.toLowerCase())
      )
    : (listQuery.data?.pages ?? []).flatMap((p) => p.posts);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!currentWorkspace) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-muted-foreground">Select a workspace to view posts</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.value
                ? 'border-accent text-accent'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search + Bulk */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search posts..."
            className="w-full bg-input border border-border rounded-md pl-9 pr-3 py-2 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        {selected.size > 0 && (
          <button
            onClick={() => {
              if (window.confirm(`Delete ${selected.size} post(s)? This cannot be undone.`))
                bulkDelete.mutate([...selected]);
            }}
            className="flex items-center gap-1.5 px-3 py-2 text-xs text-destructive bg-destructive/10 rounded-md hover:bg-destructive/20"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete {selected.size}
          </button>
        )}
      </div>

      {actionError && (
        <div className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" />
          <span className="flex-1">{actionError}</span>
          <button onClick={() => setActionError(null)} className="hover:opacity-70" aria-label="Dismiss">
            <XCircle className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Reject modal */}
      {rejectingId && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-sm space-y-4">
            <h3 className="text-sm font-semibold text-foreground">Reject Post</h3>
            <textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="Add a note explaining why (optional)..."
              className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none resize-none h-24 focus:ring-1 focus:ring-accent"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setRejectingId(null); setRejectNote(''); }}
                className="px-3 py-1.5 text-xs text-muted-foreground bg-tertiary ring-1 ring-border rounded-md hover:bg-tertiary/70"
              >
                Cancel
              </button>
              <button
                onClick={() => rejectPost.mutate({ postId: rejectingId, note: rejectNote })}
                disabled={rejectPost.isPending}
                className="px-3 py-1.5 text-xs text-destructive-foreground bg-destructive rounded-md hover:bg-destructive/90 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Posts list */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-sm text-muted-foreground">No posts found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {posts.map((post: Post) => {
            const targets = postTargets(post, accountsById);
            const failedTargets = targets.filter((t) => t.state === 'failed');
            const succeeded = targets.filter((t) => t.state === 'success').length;
            const isOutcome = post.status === 'published' || post.status === 'failed';
            // Something left to retry: a failed (or never-attempted) account
            // that is still connected.
            const canRetry =
              isOutcome &&
              post.approval_status !== 'pending' &&
              targets.some((t) => t.state !== 'success' && accountsById.has(t.id));
            const isPending = post.approval_status === 'pending';
            const isDraft = post.status === 'draft' && post.approval_status !== 'pending';
            return (
              <div
                key={post.id}
                className={`bg-card border rounded-lg px-4 py-3 flex items-start gap-3 transition-colors ${
                  selected.has(post.id) ? 'border-accent/50' : 'border-border'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(post.id)}
                  onChange={() => toggleSelect(post.id)}
                  className="mt-0.5 accent-accent"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground line-clamp-2">{post.content}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[post.status] ?? 'bg-muted text-muted-foreground'}`}>
                      {post.status}
                    </span>
                    {post.status === 'published' && failedTargets.length > 0 && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-warning/10 text-warning font-medium">
                        Partially published
                      </span>
                    )}
                    {isPending && (
                      <span className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-warning/10 text-warning font-medium">
                        <Clock className="h-3 w-3" />
                        Awaiting approval
                      </span>
                    )}
                    {post.approval_status === 'rejected' && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive font-medium">
                        Rejected
                      </span>
                    )}
                    {post.scheduled_at && (
                      <span className="text-xs text-muted-foreground">
                        {parseDbDate(post.scheduled_at).toLocaleString()}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {formatRelativeTime(post.created_at)}
                    </span>
                    {targets.map((t) => (
                      <span
                        key={t.id}
                        title={t.error ?? undefined}
                        className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full ${
                          isOutcome ? TARGET_COLORS[t.state] : TARGET_COLORS.pending
                        }`}
                      >
                        {isOutcome && t.state === 'success' && <CheckCircle2 className="h-3 w-3 shrink-0" />}
                        {isOutcome && t.state === 'failed' && <XCircle className="h-3 w-3 shrink-0" />}
                        {t.platform && <PlatformIcon platform={t.platform} size="xs" variant="glyph" />}
                        {t.label}
                      </span>
                    ))}
                  </div>
                  {isOutcome && failedTargets.length > 0 && (
                    <div className="mt-2 flex items-start gap-2 rounded-md bg-destructive/10 px-2.5 py-2 text-xs text-destructive">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" />
                      <div className="flex-1 min-w-0 space-y-0.5">
                        <p className="font-medium">
                          {succeeded > 0
                            ? `Published to ${succeeded} of ${targets.length} — ${failedTargets.length} failed`
                            : `Failed on ${failedTargets.length === targets.length ? 'every account' : `${failedTargets.length} of ${targets.length} accounts`}`}
                        </p>
                        {failedTargets.map((t) => (
                          <p key={t.id} className="break-words">
                            {t.label}: {t.error ?? 'Unknown error'}
                          </p>
                        ))}
                      </div>
                      {canRetry && (
                        <Button
                          variant="secondary"
                          onClick={() => retryFailed.mutate(post.id)}
                          loading={retryFailed.isPending && retryFailed.variables === post.id}
                          title="Re-publish only to the accounts that failed"
                        >
                          {!(retryFailed.isPending && retryFailed.variables === post.id) && (
                            <RefreshCw className="h-3 w-3" />
                          )}
                          Retry failed
                        </Button>
                      )}
                    </div>
                  )}
                  {isOutcome && failedTargets.length === 0 && canRetry && (
                    <div className="mt-2">
                      <Button
                        variant="secondary"
                        onClick={() => retryFailed.mutate(post.id)}
                        loading={retryFailed.isPending && retryFailed.variables === post.id}
                        title="Publish to the accounts this post never reached"
                      >
                        Publish to remaining accounts
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {/* Submit for approval (draft posts) */}
                  {isDraft && (
                    <button
                      onClick={() => submitForApproval.mutate(post.id)}
                      disabled={submitForApproval.isPending}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-warning bg-warning/10 rounded-md hover:bg-warning/20"
                      title="Submit for approval"
                    >
                      <Send className="h-3 w-3" />
                      Review
                    </button>
                  )}
                  {/* Approve/Reject (pending posts) */}
                  {isPendingApprovalTab && isPending && (
                    <>
                      <button
                        onClick={() => approvePost.mutate(post.id)}
                        disabled={approvePost.isPending}
                        className="p-1.5 text-success hover:bg-success/10 rounded-md"
                        title="Approve"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => setRejectingId(post.id)}
                        className="p-1.5 text-destructive hover:bg-destructive/10 rounded-md"
                        title="Reject"
                      >
                        <XCircle className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                  {onEdit && ['draft', 'scheduled', 'failed', 'recurring'].includes(post.status) && (
                    <button
                      onClick={() => onEdit(post.id)}
                      className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted/40"
                      title="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {onEdit && post.status === 'published' && (
                    <button
                      onClick={() => postAgain.mutate(post.id)}
                      disabled={postAgain.isPending}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-accent bg-accent/10 rounded-md hover:bg-accent/20 disabled:opacity-50"
                      title="Duplicate this post and open it to re-schedule"
                    >
                      <RotateCw className="h-3 w-3" />
                      Post again
                    </button>
                  )}
                  {post.status === 'published' && (
                    <button
                      onClick={() =>
                        toggleEvergreen.mutate({ id: post.id, on: !post.is_evergreen })
                      }
                      disabled={toggleEvergreen.isPending}
                      className={
                        post.is_evergreen
                          ? 'flex items-center gap-1 px-2 py-1 text-xs text-success bg-success/10 rounded-md hover:bg-success/20 disabled:opacity-50'
                          : 'p-1.5 text-muted-foreground hover:text-success rounded-md hover:bg-success/10 disabled:opacity-50'
                      }
                      title={
                        post.is_evergreen
                          ? `Evergreen — re-queued every ${post.recycle_interval_days ?? 30} days (${post.recycle_count ?? 0}/${post.max_recycles ?? 5} recycles used). Click to stop.`
                          : 'Mark evergreen: automatically re-queue this post every 30 days'
                      }
                    >
                      <Recycle className="h-3.5 w-3.5" />
                      {post.is_evergreen ? 'Evergreen' : null}
                    </button>
                  )}
                  <button
                    onClick={() => duplicatePost.mutate(post.id)}
                    className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted/40"
                    title="Duplicate"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Delete this post? This cannot be undone.'))
                        deletePost.mutate(post.id);
                    }}
                    className="p-1.5 text-muted-foreground hover:text-destructive rounded-md hover:bg-destructive/10"
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
          {!isPendingApprovalTab && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-muted-foreground">
                Showing {posts.length} of {total}
              </span>
              {listQuery.hasNextPage && (
                <Button
                  variant="secondary"
                  onClick={() => listQuery.fetchNextPage()}
                  loading={listQuery.isFetchingNextPage}
                >
                  Load more
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
